import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from './ToastContext';

function CollectionEntryForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  // Toast notifications
  const { showToast } = useToast();

  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: '',
    amount_collected: '',
    pdc: '',
    discount_amount: '',
    other_deductions: '',
    remarks: '',
    notes: '',
    due_date_text: '',
    commitment_date: '',
    commitment_amount: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCustomers();
    if (isEdit) {
      fetchEntry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (err) {
      setError('Failed to load customers.');
    }
  };

  const fetchEntry = async () => {
    try {
      const response = await api.get(`/collection-entries/${id}`);
      const entry = response.data;
      setFormData({
        customer_id: entry.customer_id,
        amount_collected: entry.amount_collected || '',
        pdc: entry.pdc || '',
        discount_amount: entry.discount_amount || '',
        other_deductions: entry.other_deductions || '',
        remarks: entry.remarks || '',
        notes: entry.notes || '',
        due_date_text: entry.due_date_text || '',
        commitment_date: entry.commitment_date ? entry.commitment_date.split('T')[0] : '',
        commitment_amount: entry.commitment_amount || '',
        entry_date: entry.entry_date ? entry.entry_date.split('T')[0] : ''
      });
    } catch (err) {
      setError('Failed to load collection entry.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/collection-entries/${id}`, formData);
        showToast('✓ Collection entry updated successfully!', 'success', 4000);
      } else {
        await api.post('/collection-entries', formData);
        showToast('✓ New collection entry created!', 'success', 4000);
      }
      navigate('/agingreports');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save collection entry.';
      showToast(`✗ ${errorMsg}`, 'error', 8000);
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Edit Collection Entry' : 'Add Collection Entry'}</h1>
      </div>

      <div className="card">
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Customer</label>
            <select
              name="customer_id"
              className="form-control"
              value={formData.customer_id}
              onChange={handleChange}
              required
              disabled={isEdit}
            >
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.customer_no} - {customer.customer_code || ''} - {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Amount Collected</label>
              <input
                type="number"
                step="0.01"
                name="amount_collected"
                className="form-control"
                value={formData.amount_collected}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>PDC</label>
              <input
                type="number"
                step="0.01"
                name="pdc"
                className="form-control"
                value={formData.pdc}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Discount Amount</label>
              <input
                type="number"
                step="0.01"
                name="discount_amount"
                className="form-control"
                value={formData.discount_amount}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Other Deductions</label>
              <input
                type="number"
                step="0.01"
                name="other_deductions"
                className="form-control"
                value={formData.other_deductions}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Remarks</label>
              <input
                type="text"
                name="remarks"
                className="form-control"
                value={formData.remarks}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="text"
                name="due_date_text"
                className="form-control"
                value={formData.due_date_text}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              className="form-control"
              rows="3"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Commitment Date</label>
              <input
                type="date"
                name="commitment_date"
                className="form-control"
                value={formData.commitment_date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Commitment Amount</label>
              <input
                type="number"
                step="0.01"
                name="commitment_amount"
                className="form-control"
                value={formData.commitment_amount}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Entry Date</label>
              <input
                type="date"
                name="entry_date"
                className="form-control"
                value={formData.entry_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CollectionEntryForm;
